import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Droplets, Waves, Wind, Lock, Facebook, Mail } from "lucide-react";
import confetti from "canvas-confetti";
import api, { apiError, PUBLIC_SITE_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const BADGE_ICONS = { "First Ripple": Droplets, "Wave Maker": Waves, "Tsunami": Wind };
const CONFETTI_COLORS = ["#41c62a", "#ffffff", "#c9cfe4", "#6d5bd0"];

const fireConfetti = () => {
  confetti({ particleCount: 100, spread: 80, origin: { y: 0.35 }, colors: CONFETTI_COLORS });
  setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 60, origin: { x: 0, y: 0.6 }, colors: CONFETTI_COLORS }), 250);
  setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 60, origin: { x: 1, y: 0.6 }, colors: CONFETTI_COLORS }), 450);
};

const WhatsAppIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export default function MyRipples() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [celebration, setCelebration] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/referrals/me").then((r) => {
      setData(r.data);
      const seenKey = `orec_badges_seen_${user.id}`;
      const seen = JSON.parse(localStorage.getItem(seenKey) || "[]");
      const earned = r.data.badges.filter((b) => b.earned).map((b) => b.name);
      const fresh = earned.filter((n) => !seen.includes(n));
      if (fresh.length) {
        const badge = r.data.badges.find((b) => b.name === fresh[fresh.length - 1]);
        setCelebration(badge);
        fireConfetti();
      }
      localStorage.setItem(seenKey, JSON.stringify(earned));
    }).catch((e) => toast.error(apiError(e)));
  }, [user]);

  if (!data) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  const inviteUrl = `${PUBLIC_SITE_URL}/register?ref=${data.ref_code}`;
  const shareText = `Join me on O.R.E.C — a community of vetted wellness practitioners. Every signup adds to my Ripple: ${inviteUrl}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = inviteUrl; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Invite link copied — go make some Ripples");
    setTimeout(() => setCopied(false), 2500);
  };

  const shareLinks = [
    { label: "WhatsApp", icon: <WhatsAppIcon />, href: `https://wa.me/?text=${encodeURIComponent(shareText)}`, testid: "share-whatsapp-btn" },
    { label: "Facebook", icon: <Facebook size={15} />, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(inviteUrl)}`, testid: "share-facebook-btn" },
    { label: "Email", icon: <Mail size={15} />, href: `mailto:?subject=${encodeURIComponent("Join me on O.R.E.C")}&body=${encodeURIComponent(shareText)}`, testid: "share-email-btn" },
  ];

  const toNext = data.next_badge ? data.next_badge.threshold - data.count : 0;
  const CelebIcon = celebration ? (BADGE_ICONS[celebration.name] || Droplets) : null;

  return (
    <div className="silk-hero min-h-[70vh] px-5 sm:px-8 py-16">
      <SEO title="My Ripples — O.R.E.C" description="Invite friends to O.R.E.C and watch your ripple effect grow." />

      {celebration && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/80 backdrop-blur-sm px-5" data-testid="badge-celebration">
          <div className="silk-card rounded-lg p-10 max-w-sm w-full text-center relative">
            <span className="absolute inset-0 rounded-lg border border-primary/20 animate-ping pointer-events-none" style={{ animationDuration: "2.2s" }} />
            <div className="w-16 h-16 mx-auto rounded-full border-2 border-primary text-primary flex items-center justify-center mb-5">
              <CelebIcon size={28} />
            </div>
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Badge unlocked</p>
            <h2 className="font-display text-3xl text-foreground mb-3">{celebration.name}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-7">{celebration.description}</p>
            <Button className="rounded-full px-8" data-testid="badge-celebration-close" onClick={() => { fireConfetti(); setCelebration(null); }}>
              Keep rippling ✦
            </Button>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto" data-testid="my-ripples-page">
        <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">Ripple Referrals</p>
        <h1 className="font-display text-4xl sm:text-5xl text-foreground mb-3">My Ripples</h1>
        <p className="text-sm text-muted-foreground mb-10 max-w-xl leading-relaxed">
          Every person you invite becomes part of your Ripple effect. Share your link, grow the community, earn badges.
        </p>

        <div className="silk-card rounded-lg p-8 mb-8 flex flex-col sm:flex-row items-center gap-8">
          <div className="relative w-28 h-28 shrink-0 flex items-center justify-center">
            <span className="absolute inset-0 rounded-full border border-primary/40 animate-ping" style={{ animationDuration: "2.4s" }} />
            <span className="absolute inset-3 rounded-full border border-primary/25 animate-ping" style={{ animationDuration: "2.4s", animationDelay: "0.6s" }} />
            <div className="w-20 h-20 rounded-full border border-primary/60 bg-card flex flex-col items-center justify-center">
              <span className="font-display text-3xl text-foreground" data-testid="ripple-count">{data.count}</span>
              <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">ripples</span>
            </div>
          </div>
          <div className="flex-1 w-full">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Your personal invite link</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate text-sm text-foreground bg-background/60 border border-border rounded-md px-3 py-2.5" data-testid="ripple-invite-link">
                {inviteUrl}
              </code>
              <Button size="sm" className="rounded-full px-4 shrink-0" onClick={copy} data-testid="ripple-copy-btn">
                {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="ripple-share-buttons">
              {shareLinks.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={s.testid}
                  className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors">
                  {s.icon} {s.label}
                </a>
              ))}
            </div>
            {data.next_badge && (
              <p className="text-xs text-muted-foreground mt-3" data-testid="ripple-next-badge">
                {toNext} more {toNext === 1 ? "invite" : "invites"} until <span className="text-primary">{data.next_badge.name}</span>
              </p>
            )}
            {!data.next_badge && <p className="text-xs text-primary mt-3">You've earned every badge — a true force of nature.</p>}
          </div>
        </div>

        <h2 className="text-base md:text-lg font-display text-foreground mb-4">Community badges</h2>
        <div className="grid sm:grid-cols-3 gap-4 mb-12" data-testid="ripple-badges">
          {data.badges.map((b) => {
            const Icon = BADGE_ICONS[b.name] || Droplets;
            return (
              <div key={b.name} data-testid={`badge-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`silk-card rounded-lg p-6 text-center transition-opacity ${b.earned ? "" : "opacity-45"}`}>
                <div className={`w-12 h-12 mx-auto rounded-full border flex items-center justify-center mb-3 ${b.earned ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                  {b.earned ? <Icon size={20} /> : <Lock size={16} />}
                </div>
                <p className="font-display text-lg text-foreground">{b.name}</p>
                <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{b.threshold} {b.threshold === 1 ? "invite" : "invites"}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{b.description}</p>
              </div>
            );
          })}
        </div>

        <h2 className="text-base md:text-lg font-display text-foreground mb-4">People in your ripple</h2>
        <div className="space-y-2" data-testid="ripple-people">
          {data.referrals.map((r, i) => (
            <div key={i} className="silk-card rounded-lg px-4 py-3 flex items-center justify-between gap-3">
              <p className="text-sm text-foreground truncate">{r.name}</p>
              <span className="text-xs text-muted-foreground shrink-0 capitalize">{r.role}</span>
            </div>
          ))}
          {data.referrals.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="ripple-empty">
              No Ripples yet — share your link above and watch the community grow because of you.
            </p>
          )}
        </div>

        <h2 className="text-base md:text-lg font-display text-foreground mt-12 mb-4">Account settings</h2>
        <AccountSettings user={user} />
      </div>
    </div>
  );
}

const AccountSettings = ({ user }) => {
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-email", { new_email: newEmail, current_password: currentPassword, origin_url: window.location.origin });
      setSentTo(newEmail);
      setNewEmail("");
      setCurrentPassword("");
      toast.success(data.message);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="silk-card rounded-lg p-6 sm:p-8" data-testid="account-settings-card">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Signed in as</p>
      <p className="text-sm text-foreground mb-6" data-testid="account-current-email">{user.email}</p>
      {sentTo ? (
        <div data-testid="change-email-sent">
          <p className="text-sm text-muted-foreground leading-relaxed">
            We've sent a confirmation link to <span className="text-foreground">{sentTo}</span>. Your email changes once you click it — the link expires in 1 hour.
          </p>
          <button onClick={() => setSentTo("")} className="cream-link text-sm mt-3" data-testid="change-email-again-btn">Use a different address</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label htmlFor="new-email" className="text-xs uppercase tracking-wider text-muted-foreground block mb-2">New email address</label>
              <input id="new-email" data-testid="change-email-input" type="email" required value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)} placeholder="new@email.com"
                className="w-full h-10 rounded-md border border-border bg-background/60 px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60" />
            </div>
            <div className="flex-1">
              <label htmlFor="current-password" className="text-xs uppercase tracking-wider text-muted-foreground block mb-2">Current password</label>
              <input id="current-password" data-testid="change-email-password-input" type="password" value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)} placeholder="For your security"
                className="w-full h-10 rounded-md border border-border bg-background/60 px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60" />
            </div>
          </div>
          <Button data-testid="change-email-submit-btn" type="submit" disabled={busy || !newEmail} className="rounded-full px-6 h-10">
            {busy ? "Sending…" : "Send confirmation"}
          </Button>
        </form>
      )}
      <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
        For your security, we'll email a confirmation link to the new address — nothing changes until you click it.
      </p>
    </div>
  );
};
